import { z } from "zod";
import { CANCEL_REASONS, CASE_STATUSES } from "./case-status.js";
import { ELIGIBILITY_REASONS } from "./patient.js";
import { ANLIEGEN, BESCHWERDEDAUER, SCHWEREGRAD, TRIAGE_RESULTS, URGENCIES } from "./triage.js";
import { SCHNELLTEST_ERGEBNISSE, VITAL_TYPES } from "./vitals.js";

/**
 * Projektionen (ARCHITEKTUR §4): die EINZIGEN Ausgabeformen eines Falls.
 * Der Server schickt jede Antwort durch das passende Schema; unbekannte Felder werden dabei entfernt,
 * sodass versehentlich hinzugefügte Daten nie ausgeliefert werden.
 */

const iso = z.string();
const isoOrNull = iso.nullable();
const status = z.enum(CASE_STATUSES);
const urgency = z.enum(URGENCIES).nullable();

export const patientOutSchema = z.object({
  vorname: z.string(),
  name: z.string(),
  strasse: z.string(),
  plz: z.string(),
  ort: z.string(),
  kvnr: z.string(),
  krankenkasse: z.string(),
  ik: z.string(),
  geburtsdatum: z.string(),
  telefon: z.string(),
});

export const vitalValueSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("rr"), sys: z.number(), dia: z.number() }),
  z.object({ type: z.literal("puls"), value: z.number() }),
  z.object({ type: z.literal("temp"), value: z.number() }),
  z.object({ type: z.literal("spo2"), value: z.number() }),
  z.object({ type: z.literal("blutzucker"), value: z.number(), unit: z.literal("mg/dl") }),
  z.object({ type: z.literal("schnelltest"), test: z.string(), ergebnis: z.enum(SCHNELLTEST_ERGEBNISSE) }),
]);

export const vitalRecordSchema = z.object({
  id: z.string(),
  value: vitalValueSchema,
  plausibility: z.enum(["ok", "warnung"]),
  warnings: z.array(z.string()),
  requestId: z.string().nullable(),
  recordedAt: iso,
});

export const vitalRequestOutSchema = z.object({
  id: z.string(),
  items: z.array(z.enum(VITAL_TYPES)),
  note: z.string(),
  status: z.enum(["offen", "erfuellt", "zurueckgezogen"]),
  requestedAt: iso,
  resolvedAt: isoOrNull,
});

/** Triage-Zusammenfassung (ohne Rohdaten des Patienten) */
export const triageOutSchema = z.object({
  source: z.enum(["smed", "omony_bogen", "entfaellt"]),
  result: z.enum(TRIAGE_RESULTS),
  urgency: z.enum(URGENCIES),
  anliegen: z.union([z.enum(ANLIEGEN), z.literal("folgekontakt")]).nullable(),
  dauer: z.enum(BESCHWERDEDAUER).nullable(),
  schwere: z.enum(SCHWEREGRAD).nullable(),
  /** Warnzeichen bzw. Ausschlussgründe (Codes) */
  reasons: z.array(z.string()),
  smed: z
    .object({
      pointOfCareCode: z.string(),
      pointOfCareText: z.string(),
      timeToTreatCode: z.string(),
      timeToTreatText: z.string(),
      mainSymptom: z.string(),
      relatedSymptoms: z.array(z.string()),
      verified: z.boolean(),
    })
    .nullable(),
});
export type TriageOut = z.infer<typeof triageOutSchema>;

const doctorRef = z
  .object({ id: z.string(), displayName: z.string(), specialty: z.string().nullable() })
  .nullable();

const billingOut = z.object({
  code: z.string().nullable(),
  amountCents: z.number().nullable(),
  status: z.enum(["offen", "eingereicht", "entfaellt"]).nullable(),
  submittedAt: isoOrNull,
  serviceTriage: z.boolean(),
  serviceVideo: z.boolean(),
});

const timesOut = z.object({
  createdAt: iso,
  queuedAt: isoOrNull,
  acceptedAt: isoOrNull,
  consultationStartedAt: isoOrNull,
  completedAt: isoOrNull,
  cancelledAt: isoOrNull,
});

/** Vollansicht der eigenen Apotheke */
export const caseForPharmacySchema = z.object({
  id: z.string(),
  reference: z.string(),
  status,
  version: z.number(),
  patientLanguage: z.string(),
  insuranceType: z.enum(["gkv", "pkv"]),
  knownPatient: z.boolean(),
  doctorSource: z.enum(["omony_netz", "lokal"]),
  patient: patientOutSchema.nullable(),
  eligibility: z.array(z.enum(ELIGIBILITY_REASONS)),
  consentAt: isoOrNull,
  patientCaptureSource: z.enum(["vision", "lokal_ocr", "manuell"]).nullable(),
  patientConfirmedAt: isoOrNull,
  triage: triageOutSchema.nullable(),
  urgency,
  label: z.object({ initials: z.string(), age: z.number().nullable() }),
  signageNumber: z.number().nullable(),
  doctor: doctorRef,
  cancelReason: z.enum(CANCEL_REASONS).nullable(),
  cancelledByRole: z.enum(["apotheke", "arzt", "system"]).nullable(),
  prescriptionIssued: z.boolean().nullable(),
  noteToPharmacy: z.string().nullable(),
  vitals: z.array(vitalRecordSchema),
  vitalRequests: z.array(vitalRequestOutSchema),
  smedSession: z
    .object({ id: z.string(), status: z.string(), embedMode: z.enum(["iframe", "redirect"]).nullable() })
    .nullable(),
  videoStatus: z.enum(["angelegt", "aktiv", "beendet"]).nullable(),
  billing: billingOut,
  draft: z.unknown().nullable(),
  anonymized: z.boolean(),
  times: timesOut,
});
export type CaseForPharmacy = z.infer<typeof caseForPharmacySchema>;

/** Kurzform für Listen der Apotheke */
export const caseSummarySchema = z.object({
  id: z.string(),
  reference: z.string(),
  status,
  version: z.number(),
  urgency,
  label: z.object({ initials: z.string(), age: z.number().nullable() }),
  signageNumber: z.number().nullable(),
  doctorName: z.string().nullable(),
  openVitalRequests: z.number(),
  billingStatus: z.enum(["offen", "eingereicht", "entfaellt"]).nullable(),
  times: timesOut,
});
export type CaseSummary = z.infer<typeof caseSummarySchema>;

/** Warteschlange für Ärzt:innen – anonym: Initialen + Alter, keine Stammdaten */
export const queueItemForDoctorSchema = z.object({
  id: z.string(),
  status,
  version: z.number(),
  pharmacy: z.object({ name: z.string(), city: z.string() }),
  label: z.object({ initials: z.string(), age: z.number().nullable() }),
  urgency,
  triage: triageOutSchema.nullable(),
  insuranceType: z.enum(["gkv", "pkv"]),
  knownPatient: z.boolean(),
  doctorSource: z.enum(["omony_netz", "lokal"]),
  patientLanguage: z.string(),
  vitals: z.array(vitalRecordSchema),
  queuedAt: isoOrNull,
});
export type QueueItemForDoctor = z.infer<typeof queueItemForDoctorSchema>;

/** Laufender Fall eines Kollegen (Board-Übersicht) – anonym, ohne Gesundheitsdaten */
export const activeCaseOverviewSchema = z.object({
  id: z.string(),
  status,
  pharmacy: z.object({ name: z.string(), city: z.string() }),
  label: z.object({ initials: z.string(), age: z.number().nullable() }),
  urgency,
  doctor: doctorRef,
  acceptedAt: isoOrNull,
  consultationStartedAt: isoOrNull,
});
export type ActiveCaseOverview = z.infer<typeof activeCaseOverviewSchema>;

/** Vollansicht für die zugeteilte Ärztin / den zugeteilten Arzt */
export const caseForAssignedDoctorSchema = queueItemForDoctorSchema.extend({
  reference: z.string(),
  patient: patientOutSchema.nullable(),
  eligibility: z.array(z.enum(ELIGIBILITY_REASONS)),
  doctor: doctorRef,
  vitalRequests: z.array(vitalRequestOutSchema),
  notes: z.string(),
  prescriptionIssued: z.boolean().nullable(),
  noteToPharmacy: z.string().nullable(),
  cancelReason: z.enum(CANCEL_REASONS).nullable(),
  videoStatus: z.enum(["angelegt", "aktiv", "beendet"]).nullable(),
  readOnly: z.boolean(),
  times: timesOut,
});
export type CaseForAssignedDoctor = z.infer<typeof caseForAssignedDoctorSchema>;

/** Kiosk im Patientenmodus (ohne PIN): nur das Nötigste für die Patientin / den Patienten */
export const patientViewSchema = z.object({
  caseId: z.string(),
  status,
  patientLanguage: z.string(),
  signageNumber: z.number().nullable(),
  doctorName: z.string().nullable(),
  vitalsRequested: z.boolean(),
  videoAvailable: z.boolean(),
});
export type PatientView = z.infer<typeof patientViewSchema>;

/** Wartebereich-Anzeige: anonyme Nummern */
export const signageTicketSchema = z.object({
  number: z.number(),
  state: z.enum(["wartend", "aufgerufen"]),
  urgent: z.boolean(),
});
export type SignageTicket = z.infer<typeof signageTicketSchema>;

/** Verwaltung: nur Metadaten, keine Gesundheitsdaten */
export const caseMetaForAdminSchema = z.object({
  id: z.string(),
  reference: z.string(),
  pharmacyId: z.string(),
  status,
  createdAt: iso,
  completedAt: isoOrNull,
  cancelledAt: isoOrNull,
  billingCode: z.string().nullable(),
  anonymized: z.boolean(),
});
export type CaseMetaForAdmin = z.infer<typeof caseMetaForAdminSchema>;

/** Board für den Leitstand */
export const boardSchema = z.object({
  queue: z.array(queueItemForDoctorSchema),
  active: z.array(activeCaseOverviewSchema),
  mine: z.array(z.object({ id: z.string(), status, version: z.number() })),
  doctors: z.array(
    z.object({
      id: z.string(),
      displayName: z.string(),
      specialty: z.string().nullable(),
      presence: z.enum(["online", "pause", "offline"]),
      busy: z.boolean(),
    }),
  ),
  kpis: z.object({
    waiting: z.number(),
    waitingRot: z.number(),
    active: z.number(),
    completedToday: z.number(),
    medianWaitMinutesToday: z.number().nullable(),
  }),
  me: z.object({
    presence: z.enum(["online", "pause", "offline"]),
    activeCount: z.number(),
    maxActive: z.number(),
  }),
  serverTime: iso,
});
export type Board = z.infer<typeof boardSchema>;
